import type { PrecioTipo } from '../shared/types/autorizacion';
import type { EmissionScheduleClient } from '../shared/types/venta-form';
import { $ } from '../shared/utils/dom';
import { computeFechaVencimiento, VENCIMIENTO_DIAS_VALIDOS } from '../shared/utils/emision-schedule';
import { fmtFechaDia } from '../shared/utils/format';
import { fetchEmissionSchedule } from '../shared/services/emision-schedule-api';
import { toast } from '../shared/utils/toast';

const POLL_INTERVAL_MS = 60_000;

export interface EmissionUiLike {
  schedule: EmissionScheduleClient | null;
  renderVencimientoInfo(precioTipo: PrecioTipo, vencimientoDias: number | null): void;
}

export function renderVencimientoInfoEl(
  schedule: EmissionScheduleClient | null,
  precioTipo: PrecioTipo,
  vencimientoDias: number | null
): void {
  const el = $('vencimiento-info');
  if (!el || !schedule?.fechaEmision) {
    if (el) el.textContent = '';
    return;
  }
  if (precioTipo === 'mercado') {
    el.textContent =
      'Vencimiento: misma fecha de emisión (' +
      fmtFechaDia(schedule.fechaEmision) +
      ').';
    return;
  }
  const dias = vencimientoDias || 7;
  const fv = computeFechaVencimiento(schedule.fechaEmision, 'base', dias);
  el.textContent = fv
    ? 'Vencimiento: ' + fmtFechaDia(fv) + ' (' + dias + ' días desde la emisión).'
    : '';
}

export class EmissionUiController implements EmissionUiLike {
  schedule: EmissionScheduleClient | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  renderVencimientoInfo(precioTipo: PrecioTipo, vencimientoDias: number | null): void {
    renderVencimientoInfoEl(this.schedule, precioTipo, vencimientoDias);
  }

  renderSchedule(precioTipo: PrecioTipo, vencimientoDias: number | null): void {
    if (!this.schedule) return;
    const display = $('fecha-emision-display');
    if (display) display.textContent = fmtFechaDia(this.schedule.fechaEmision);

    const alertEl = $('fecha-emision-alert');
    const hintEl = $('fecha-emision-hint');
    const pastCutoff = this.schedule.beforeCutoff === false;

    if (alertEl && hintEl) {
      if (pastCutoff) {
        alertEl.textContent =
          'Pasadas las ' +
          (this.schedule.cutoffLabel || '15:00') +
          ' (Argentina), la autorización se emitirá con fecha del día siguiente (' +
          fmtFechaDia(this.schedule.fechaEmision) +
          ').';
        alertEl.classList.remove('hidden');
        hintEl.classList.add('hidden');
      } else {
        alertEl.classList.add('hidden');
        hintEl.classList.remove('hidden');
        hintEl.textContent =
          'Hasta las ' +
          (this.schedule.cutoffLabel || '15:00') +
          ' (Argentina) se emite con fecha de hoy.';
      }
    }
    this.renderVencimientoInfo(precioTipo, vencimientoDias);
  }

  async load(precioTipo: PrecioTipo, vencimientoDias: number | null): Promise<void> {
    try {
      const data = await fetchEmissionSchedule();
      const prevFecha = this.schedule?.fechaEmision;
      this.schedule = data;
      this.renderSchedule(precioTipo, vencimientoDias);
      if (prevFecha && prevFecha !== data.fechaEmision) {
        toast('La fecha de emisión cambió. Verificá antes de confirmar.');
      }
    } catch {
      this.schedule = null;
      const display = $('fecha-emision-display');
      const hint = $('fecha-emision-hint');
      const alert = $('fecha-emision-alert');
      if (display) display.textContent = '—';
      if (hint) {
        hint.textContent = 'No se pudo obtener la fecha de emisión. Reintentá más tarde.';
        hint.classList.remove('hidden');
      }
      alert?.classList.add('hidden');
    }
  }

  startPoll(precioTipo: PrecioTipo, vencimientoDias: number | null): void {
    this.stopPoll();
    this.pollTimer = setInterval(() => {
      void this.load(precioTipo, vencimientoDias);
    }, POLL_INTERVAL_MS);
  }

  stopPoll(): void {
    if (this.pollTimer != null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}

export function isValidVencimientoDias(dias: number | null): dias is number {
  return (
    dias != null &&
    VENCIMIENTO_DIAS_VALIDOS.includes(dias as (typeof VENCIMIENTO_DIAS_VALIDOS)[number])
  );
}
